/**
 * The Reject half of an invite card, shared by the Invite tab and the Invited
 * pools strip on Pools (ISSUE-1346): the named destructive confirm, the
 * decline call, and the rejected rows kept in place so the fan sees what
 * they turned down until they leave.
 */
import { useInviteDecline } from "@sportsmet/pool-sdk/private";
import { confirmDestructive } from "@sportsmet/ui";
import { useState } from "react";

import { showApiError, showOfflineError } from "../../errors/apiErrorBus";
import type { typeInviteRow, typeRejectedEntry } from "./invitesListState";

export type typeInviteRejectFlow = {
  rejected: ReadonlyMap<string, typeRejectedEntry>;
  rejectingId: string | null;
  handleReject: (inviteId: string) => Promise<void>;
};

export function useInviteRejectFlow(
  items: readonly typeInviteRow[] | null,
): typeInviteRejectFlow {
  const [rejected, setRejected] = useState<
    ReadonlyMap<string, typeRejectedEntry>
  >(new Map());
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const decline = useInviteDecline();

  const handleReject = async (inviteId: string): Promise<void> => {
    const index = (items ?? []).findIndex((row) => row.inviteId === inviteId);
    const row = (items ?? [])[index];
    if (row === undefined) return;

    // ISSUE-1426. Not now on a pool the fan opened from a link only drops
    // their own bookmark, and opening the link again brings it back, so it
    // asks nothing. Rejecting an invite someone sent still confirms.
    if (row.source !== "link") {
      const isConfirmed = await confirmDestructive({
        title: "Reject this invite?",
        body: `${row.poolName} stays off your pools. Whoever sent it would have to invite you again.`,
        confirmLabel: "Reject invite",
      });
      if (!isConfirmed) return;
    }

    setRejectingId(inviteId);
    try {
      const response = await decline.mutateAsync({ inviteId });
      if (response.error) {
        showApiError(response);
      } else {
        setRejected((prev) => new Map(prev).set(inviteId, { row, index }));
      }
    } catch {
      showOfflineError();
    } finally {
      setRejectingId(null);
    }
  };

  return { rejected, rejectingId, handleReject };
}
